import SparkMD5 from 'spark-md5';

const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB

export const calculateFileMd5 = async (
  file: File,
  log?: (
    level: 'info' | 'warn' | 'error' | 'debug',
    message: string,
    meta?: Record<string, unknown>,
  ) => void,
): Promise<string> => {
  const startTime = performance.now();
  log?.('info', 'MD5 hash calculation started', {
    fileName: file.name,
    fileSize: file.size,
  });

  try {
    const spark = new SparkMD5.ArrayBuffer();
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const buffer = await chunk.arrayBuffer();
      spark.append(buffer);
    }

    const md5 = spark.end();
    spark.destroy();

    const durationMs = Math.round(performance.now() - startTime);
    log?.('info', 'MD5 hash calculation completed', {
      fileName: file.name,
      fileSize: file.size,
      durationMs,
    });

    return md5;
  } catch (error) {
    const durationMs = Math.round(performance.now() - startTime);
    log?.('warn', 'MD5 hash calculation failed', {
      fileName: file.name,
      fileSize: file.size,
      durationMs,
      error: error instanceof Error ? error.message : String(error),
    });
    return '';
  }
};

export const calculateFileSha256 = async (file: File) => {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hashHex;
};

export const base64Encode = (input: string): string => {
  return Buffer.from(input).toString('base64');
};

export const base64ToHex = (base64Str: string) =>
  Buffer.from(base64Str, 'base64').toString('hex');

export const parseJwt = (token: string) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const payload = parts[1];
    const decodedPayload = atob(payload!.replace(/-/g, '+').replace(/_/g, '/'));
    const parsedPayload = JSON.parse(decodedPayload) as Record<string, unknown>;
    return parsedPayload;
  } catch {
    return null;
  }
};
